import { useEffect, useMemo, useState } from "react";
import CommonLayout from "../../layouts/CommonLayout";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { getAuthConfig } from "../../services/apiUtils";
import { ROUTE_CONSTANT } from "../../routes/constant";
import { toast } from "react-toastify";
import { allAPiServicesCall } from "../../services/apiServices";
import { Spinner } from "react-bootstrap";
import {
    FaMusic,
    FaSearch,
    FaTimes,
    FaEdit,
    FaTrash,
    FaChevronLeft,
    FaChevronRight,
    FaLayerGroup,
    FaSortUp,
    FaSortDown,
    FaSort
} from "react-icons/fa";
import "./AllSongs.css";

const PAGE_SIZE = 20;

function useDebouncedValue(value, delay = 400) {
    const [v, setV] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setV(value), delay);
        return () => clearTimeout(t);
    }, [value, delay]);
    return v;
}

export default function AllSongs() {
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();

    const page = Number(params.get("page") || 1);
    const sortParam = params.get("sort") || "-_id";
    const qParam = params.get("q") || "";
    const searchByParam = params.get("searchBy") || "name";
    const artistParam = params.get("artist") || "";
    const albumParam = params.get("album") || "";

    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: PAGE_SIZE,
        total: 0,
        totalPages: 1,
    });

    const [search, setSearch] = useState(qParam);
    const [searchBy, setSearchBy] = useState(searchByParam);
    const [artistFilter, setArtistFilter] = useState(artistParam);
    const [albumFilter, setAlbumFilter] = useState(albumParam);
    const debouncedQ = useDebouncedValue(search, 400);
    const debouncedArtist = useDebouncedValue(artistFilter, 400);
    const debouncedAlbum = useDebouncedValue(albumFilter, 400);

    const query = useMemo(
        () => ({
            page,
            limit: PAGE_SIZE,
            sort: sortParam,
            q: debouncedQ || undefined,
            searchBy,
            artist: debouncedArtist || undefined,
            album: debouncedAlbum || undefined,
        }),
        [page, sortParam, debouncedQ, searchBy, debouncedArtist, debouncedAlbum]
    );

    const fetchData = async () => {
        try {
            setLoading(true);
            const res = await allAPiServicesCall.listSongs(query, getAuthConfig());
            setRows(res?.data || []);
            setPagination(
                res?.pagination || { page: 1, limit: PAGE_SIZE, total: 0, totalPages: 1 }
            );
        } catch (e) {
            console.error(e);
            toast.error("Failed to load songs");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, sortParam, debouncedQ, searchBy, debouncedArtist, debouncedAlbum]);

    useEffect(() => {
        const next = new URLSearchParams(params);
        if (debouncedQ) next.set("q", debouncedQ); else next.delete("q");
        if (debouncedArtist) next.set("artist", debouncedArtist); else next.delete("artist");
        if (debouncedAlbum) next.set("album", debouncedAlbum); else next.delete("album");
        next.set("page", String(1));
        next.set("sort", sortParam || "-createdAt");
        next.set("searchBy", searchBy);
        setParams(next, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedQ, searchBy, debouncedArtist, debouncedAlbum]);

    const goToPage = (p) => {
        const next = new URLSearchParams(params);
        next.set("page", String(p));
        setParams(next);
    };

    const setSort = (nextSort) => {
        const next = new URLSearchParams(params);
        next.set("sort", nextSort);
        next.set("page", "1");
        setParams(next);
    };

    const toggleSort = (field) => {
        const isThisField = sortParam.replace("-", "") === field;
        const next = isThisField && !sortParam.startsWith("-")
            ? `-${field}`
            : (isThisField && sortParam.startsWith("-"))
                ? field
                : `-${field}`;
        setSort(next);
    };

    const SortIcon = ({ field }) => {
        const base = sortParam.replace("-", "");
        if (base !== field) return <FaSort className="songs-table-sort-icon" />;
        return sortParam.startsWith("-")
            ? <FaSortDown className="songs-table-sort-icon" />
            : <FaSortUp className="songs-table-sort-icon" />;
    };

    const handleDelete = async (song_id) => {
        const yes = window.confirm(`Delete Song #${song_id}? This cannot be undone.`);
        if (!yes) return;
        try {
            await allAPiServicesCall.deleteSong(song_id, getAuthConfig());
            toast.success("Song deleted");
            if (rows.length === 1 && pagination.page > 1) {
                goToPage(pagination.page - 1);
            } else {
                fetchData();
            }
        } catch (err) {
            console.error(err);
            toast.error(err?.response?.data?.message || "Delete failed");
        }
    };

    const editHref = (id) =>
        ROUTE_CONSTANT.COMMON.EDIT_SONG.replace(":id", id);

    const clearSearch = () => setSearch("");
    const clearArtistFilter = () => setArtistFilter("");
    const clearAlbumFilter = () => setAlbumFilter("");
    const clearAllFilters = () => {
        setSearch("");
        setArtistFilter("");
        setAlbumFilter("");
    };

    const hasActiveFilters = search || artistFilter || albumFilter;

    const getPlaceholder = () => {
        switch (searchBy) {
            case "id": return "Enter song ID...";
            case "name": return "Search by name...";
            case "genre": return "Search by genre...";
            case "slug": return "Search by slug...";
            default: return "Search...";
        }
    };

    const formatGenres = (genre) => {
        if (Array.isArray(genre)) return genre;
        if (typeof genre === "string") return genre.split(",").map(g => g.trim()).filter(Boolean);
        return [];
    };

    return (
        <CommonLayout>
            <div className="all-songs-page">
                {/* Header Section */}
                <div className="songs-header">
                    <div className="songs-header-content">
                        <div className="songs-header-icon">
                            <FaMusic />
                        </div>
                        <div className="songs-header-text">
                            <h1 className="songs-title">All Songs</h1>
                            <p className="songs-subtitle">
                                Browse and manage your song library
                            </p>
                        </div>
                    </div>
                    <div className="songs-stats">
                        <div className="songs-stat-card">
                            <FaMusic className="songs-stat-icon" />
                            <div className="songs-stat-info">
                                <span className="songs-stat-value">{pagination.total}</span>
                                <span className="songs-stat-label">Total Songs</span>
                            </div>
                        </div>
                        <div className="songs-stat-card">
                            <FaLayerGroup className="songs-stat-icon" />
                            <div className="songs-stat-info">
                                <span className="songs-stat-value">{pagination.totalPages}</span>
                                <span className="songs-stat-label">Pages</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Search Section */}
                <div className="songs-controls">
                    {/* Row 1: Main Search - Full Width */}
                    <div className="songs-search-wrapper songs-search-main">
                        <select
                            className="songs-filter-select"
                            value={searchBy}
                            onChange={(e) => setSearchBy(e.target.value)}
                        >
                            <option value="name">Name</option>
                            <option value="id">ID</option>
                            <option value="genre">Genre</option>
                            <option value="slug">Slug</option>
                        </select>
                        <FaSearch className="songs-search-icon" />
                        <input
                            type={searchBy === "id" ? "number" : "text"}
                            className="songs-search-input"
                            placeholder={getPlaceholder()}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                        {search && (
                            <button
                                className="songs-search-clear"
                                onClick={clearSearch}
                            >
                                <FaTimes />
                            </button>
                        )}
                    </div>

                    {/* Row 2: Artist & Album Filters */}
                    <div className="songs-filters-row">
                        {/* Artist Filter */}
                        <div className="songs-search-wrapper songs-filter-secondary">
                            <span className="songs-filter-label">Artist</span>
                            <input
                                type="text"
                                className="songs-search-input"
                                placeholder="Filter by artist..."
                                value={artistFilter}
                                onChange={(e) => setArtistFilter(e.target.value)}
                            />
                            {artistFilter && (
                                <button
                                    className="songs-search-clear"
                                    onClick={clearArtistFilter}
                                >
                                    <FaTimes />
                                </button>
                            )}
                        </div>

                        {/* Album Filter */}
                        <div className="songs-search-wrapper songs-filter-secondary">
                            <span className="songs-filter-label">Album</span>
                            <input
                                type="text"
                                className="songs-search-input"
                                placeholder="Filter by album..."
                                value={albumFilter}
                                onChange={(e) => setAlbumFilter(e.target.value)}
                            />
                            {albumFilter && (
                                <button
                                    className="songs-search-clear"
                                    onClick={clearAlbumFilter}
                                >
                                    <FaTimes />
                                </button>
                            )}
                        </div>

                        {/* Clear All Button */}
                        {hasActiveFilters && (
                            <button
                                className="songs-clear-all-btn"
                                onClick={clearAllFilters}
                            >
                                <FaTimes /> Clear All
                            </button>
                        )}
                    </div>
                </div>

                {/* Content Section */}
                <div className="songs-content">
                    {loading ? (
                        <div className="songs-loading">
                            <div className="songs-loading-spinner">
                                <Spinner animation="border" />
                            </div>
                            <p className="songs-loading-text">Loading songs...</p>
                        </div>
                    ) : rows.length === 0 ? (
                        <div className="songs-empty">
                            <div className="songs-empty-icon">
                                <FaMusic />
                            </div>
                            <h3 className="songs-empty-title">No Songs Found</h3>
                            <p className="songs-empty-text">
                                {search ? "Try adjusting your search criteria" : "No songs available in the library"}
                            </p>
                        </div>
                    ) : (
                        <>
                            {/* Desktop Table View */}
                            <div className="songs-desktop-view">
                                <table className="songs-table">
                                    <thead>
                                        <tr>
                                            <th style={{ width: 100 }}>
                                                <button
                                                    className="songs-table-sort-btn"
                                                    onClick={() => toggleSort("song_id")}
                                                >
                                                    ID <SortIcon field="song_id" />
                                                </button>
                                            </th>
                                            <th style={{ width: 80 }}>Image</th>
                                            <th>
                                                <button
                                                    className="songs-table-sort-btn"
                                                    onClick={() => toggleSort("song_name")}
                                                >
                                                    Name <SortIcon field="song_name" />
                                                </button>
                                            </th>
                                            <th>Artist</th>
                                            <th>
                                                <button
                                                    className="songs-table-sort-btn"
                                                    onClick={() => toggleSort("genres")}
                                                >
                                                    Genres <SortIcon field="genres" />
                                                </button>
                                            </th>
                                            <th style={{ width: 180 }}>
                                                <button
                                                    className="songs-table-sort-btn"
                                                    onClick={() => toggleSort("createdAt")}
                                                >
                                                    Created <SortIcon field="createdAt" />
                                                </button>
                                            </th>
                                            <th style={{ width: 160 }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((row) => (
                                            <tr key={row.song_id}>
                                                <td>
                                                    <span className="songs-table-id">#{row.song_id}</span>
                                                </td>
                                                <td>
                                                    {row.song_image_downloaded_link || row.downloaded_image_link || row.coverImage ? (
                                                        <Link to={editHref(row.song_id)}>
                                                            <img
                                                                src={row.song_image_downloaded_link || row.coverImage}
                                                                alt={row.song_name}
                                                                className="songs-table-image"
                                                                onError={(e) => {
                                                                    e.target.onerror = null;
                                                                    e.target.src = "/no-image.png";
                                                                }}
                                                            />
                                                        </Link>
                                                    ) : (
                                                        <div className="songs-table-image-placeholder">
                                                            <FaMusic />
                                                        </div>
                                                    )}
                                                </td>
                                                <td>
                                                    <Link to={editHref(row.song_id)} className="songs-table-name">
                                                        {row.song_name}
                                                    </Link>
                                                </td>
                                                <td>
                                                    <span className="songs-table-artist">
                                                        {row.artistName || "-"}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className="songs-genre-tags">
                                                        {formatGenres(row.genre).length > 0 ? (
                                                            formatGenres(row.genre).slice(0, 3).map((g, i) => (
                                                                <span key={i} className="songs-genre-tag">{g}</span>
                                                            ))
                                                        ) : (
                                                            <span className="songs-table-date">-</span>
                                                        )}
                                                        {formatGenres(row.genre).length > 3 && (
                                                            <span className="songs-genre-tag">+{formatGenres(row.genre).length - 3}</span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className="songs-table-date">
                                                        {row.createdAt && !isNaN(new Date(row.createdAt).getTime())
                                                            ? new Date(row.createdAt).toLocaleDateString()
                                                            : "-"}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className="songs-table-actions">
                                                        <Link
                                                            to={editHref(row.song_id)}
                                                            className="songs-action-btn songs-action-btn-edit"
                                                        >
                                                            <FaEdit /> Edit
                                                        </Link>
                                                        <button
                                                            className="songs-action-btn songs-action-btn-delete"
                                                            onClick={() => handleDelete(row.song_id)}
                                                        >
                                                            <FaTrash /> Delete
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile Card View */}
                            <div className="songs-mobile-view">
                                {rows.map((row) => (
                                    <div key={row.song_id} className="songs-mobile-card">
                                        <div className="songs-mobile-card-header">
                                            {row.song_image_downloaded_link || row.coverImage ? (
                                                <img
                                                    src={row.song_image_downloaded_link || row.coverImage}
                                                    alt={row.song_name}
                                                    className="songs-mobile-card-image"
                                                    onError={(e) => {
                                                        e.target.onerror = null;
                                                        e.target.src = "/no-image.png";
                                                    }}
                                                />
                                            ) : (
                                                <div className="songs-table-image-placeholder" style={{ width: 70, height: 70 }}>
                                                    <FaMusic />
                                                </div>
                                            )}
                                            <div className="songs-mobile-card-info">
                                                <h4 className="songs-mobile-card-title">{row.song_name}</h4>
                                                {row.artistName && (
                                                    <p className="songs-mobile-card-artist">{row.artistName}</p>
                                                )}
                                                <p className="songs-mobile-card-id">#{row.song_id}</p>
                                                <div className="songs-genre-tags">
                                                    {formatGenres(row.genre).slice(0, 2).map((g, i) => (
                                                        <span key={i} className="songs-genre-tag">{g}</span>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="songs-mobile-card-actions">
                                            <Link
                                                to={editHref(row.song_id)}
                                                className="songs-action-btn songs-action-btn-edit"
                                            >
                                                <FaEdit /> Edit
                                            </Link>
                                            <button
                                                className="songs-action-btn songs-action-btn-delete"
                                                onClick={() => handleDelete(row.song_id)}
                                            >
                                                <FaTrash /> Delete
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}

                    {/* Pagination */}
                    {rows.length > 0 && (
                        <div className="songs-pagination">
                            <div className="songs-pagination-info">
                                Showing {(pagination.page - 1) * pagination.limit + 1} – {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
                            </div>
                            <div className="songs-pagination-controls">
                                <button
                                    className="songs-pagination-btn"
                                    disabled={pagination.page <= 1}
                                    onClick={() => goToPage(1)}
                                >
                                    First
                                </button>
                                <button
                                    className="songs-pagination-btn"
                                    disabled={pagination.page <= 1}
                                    onClick={() => goToPage(pagination.page - 1)}
                                >
                                    <FaChevronLeft /> Prev
                                </button>
                                <div className="songs-pagination-current">
                                    {pagination.page} / {pagination.totalPages}
                                </div>
                                <button
                                    className="songs-pagination-btn"
                                    disabled={pagination.page >= pagination.totalPages}
                                    onClick={() => goToPage(pagination.page + 1)}
                                >
                                    Next <FaChevronRight />
                                </button>
                                <button
                                    className="songs-pagination-btn"
                                    disabled={pagination.page >= pagination.totalPages}
                                    onClick={() => goToPage(pagination.totalPages)}
                                >
                                    Last
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </CommonLayout>
    );
}
